// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSettingsCollection } from "@conklin/App/Store/Selectors/SettingsSelectors";
import { mergeWithCss } from "@conklin/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";

interface MiniSignInProps {
    linkGroups?: {
        title: string;
        links: (
            | {
                  title: string;
                  url: string;
                  type?: string | undefined;
                  id: string;
                  parentId?: string | undefined;
                  excludeFromNavigation?: boolean | undefined;
                  children?: PageLinkModel[] | undefined;
                  openInNewWindow?: boolean;
              }
            | undefined
        )[];
    }[];
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStyles;
}

type Props = MiniSignInProps;

export interface MiniMyAccountStyles {
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkItems?: {
        wrapper?: InjectableCss;
        linkWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        link?: LinkPresentationProps;
    };
    personalInfo?: {
        wrapper?: InjectableCss;
        infoWrapper?: InjectableCss;
        title?: TypographyPresentationProps;
        subTitle?: TypographyPresentationProps;
        shipToInfo?: TypographyPresentationProps;
        pickUpInfo?: TypographyPresentationProps;
    };
    actionSection?: {
        wrapper?: InjectableCss;
        changeFulfillmentButton?: LinkPresentationProps;
        signOutButton?: LinkPresentationProps;
    };
}

export const miniMyAccountStyles: MiniMyAccountStyles = {};

const MiniMyAccount: FC<Props> = ({ linkGroups, onSignOutHandler, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));
    const {
        customerNumber,
        currentShipto,
        isPunchOutSession,
        changeCustomerPageUrl,
        fulfillmentMethod,
        pickUpWarehouse,
        displayChangeCustomerLink,
        lockCustomerSelection,
    } = useAppSelector(state => {
        const session = getSession(state);
        return {
            customerNumber: getBillToState(state, session.billToId).value?.customerNumber,
            currentShipto: getShipToState(state, session.shipToId).value,
            isPunchOutSession: getIsPunchOutSession(state),
            changeCustomerPageUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            fulfillmentMethod: session.fulfillmentMethod,
            pickUpWarehouse: session.pickUpWarehouse,
            displayChangeCustomerLink: session.displayChangeCustomerLink,
            lockCustomerSelection: getSettingsCollection(state).customSettings?.lockCustomerSelection,
        };
    });

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        (link, index) =>
                            link?.url && (
                                <StyledWrapper
                                    key={`${link?.title}_${link?.url}_${index}`}
                                    {...styles.linkItems?.linkWrapper}
                                >
                                    <Link
                                        href={link?.url}
                                        {...styles.linkItems?.link}
                                        target={(link as any).openInNewWindow === true ? "_blank" : ""}
                                        aria-label={link?.title}
                                    >
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.titleWrapper}>
                <Typography {...styles.title}>
                    {translate("Customer")}:{` #${customerNumber}`}
                </Typography>
            </StyledWrapper>
            {linksSection}
            <StyledWrapper {...styles.personalInfo?.wrapper}>
                <StyledWrapper {...styles.personalInfo?.infoWrapper}>
                    <Typography {...styles.personalInfo?.title}>{translate("Profile Information")}</Typography>
                    {currentShipto && fulfillmentMethod === FulfillmentMethod.Ship && (
                        <>
                            <Typography {...styles.personalInfo?.subTitle}>{translate("Shipping Address")}</Typography>
                            <Typography {...styles.personalInfo?.shipToInfo}>{`${currentShipto?.address1}`}</Typography>
                            <Typography {...styles.personalInfo?.shipToInfo}>{`${currentShipto?.address2}`}</Typography>
                            <Typography {...styles.personalInfo?.shipToInfo}>
                                {`${currentShipto?.city}, ${currentShipto?.state?.abbreviation} ${currentShipto?.postalCode}`}
                            </Typography>
                        </>
                    )}
                    {pickUpWarehouse && fulfillmentMethod === FulfillmentMethod.PickUp && (
                        <>
                            <Typography {...styles.personalInfo?.subTitle}>{translate("Pick Up Address")}</Typography>
                            <Typography
                                {...styles.personalInfo?.pickUpInfo}
                            >{`${pickUpWarehouse?.description}`}</Typography>
                            <Typography
                                {...styles.personalInfo?.pickUpInfo}
                            >{`${pickUpWarehouse?.address1}`}</Typography>
                            <Typography
                                {...styles.personalInfo?.pickUpInfo}
                            >{`${pickUpWarehouse?.address2}`}</Typography>
                            <Typography {...styles.personalInfo?.pickUpInfo}>
                                {`${pickUpWarehouse?.city}, ${pickUpWarehouse?.state} ${pickUpWarehouse?.postalCode}`}
                            </Typography>
                        </>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionSection?.wrapper}>
                {displayChangeCustomerLink && changeCustomerPageUrl && !lockCustomerSelection && (
                    <Link
                        {...styles.actionSection?.changeFulfillmentButton}
                        href={changeCustomerPageUrl}
                        aria-label="Change Fulfillment"
                        data-test-selector="headerChangeFulfillmentLink"
                    >
                        {translate("Change Fulfillment")}
                    </Link>
                )}
                <Link
                    {...styles.actionSection?.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                    }}
                    aria-label="Sign Out"
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Link>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
